import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useCart } from '../../context/CartContext';
import ProductCard from './ProductCard';
import ProductOptionsModal, { bannerCache } from './ProductOptionsModal';
import OfflineService from '../../services/OfflineService';
import logo from '../../assets/logo-getit.png';
import logoPosgo from '../../assets/logo-posgo.png';

const EXCLUDED_CATEGORIES = ['All', 'Expenses', 'Saleable', 'PoS'];

const ProductList = () => {
    const { categories, sessionData } = useAppContext();
    const { addItem, items } = useCart();
    const navigate = useNavigate();
    const { categoryId, subcategoryId } = useParams();

    const totalItems = items.reduce((sum, item) => sum + (item.quantity || 1), 0);

    const [filteredProducts, setFilteredProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isOffline, setIsOffline] = useState(!navigator.onLine);

    useEffect(() => {
        if (!sessionData || !sessionData.authenticated) {
            navigate('/login', { replace: true });
        }
    }, [sessionData, navigate]);

    useEffect(() => {
        const handleOnline = () => setIsOffline(false);
        const handleOffline = () => setIsOffline(true);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const getFilteredCategories = useCallback(() => {
        if (!categories || !Array.isArray(categories)) {
            return [];
        }

        return categories.filter(category => {
            const nameMatch = !EXCLUDED_CATEGORIES.some(excluded =>
                category.name && category.name.toLowerCase().includes(excluded.toLowerCase())
            );

            const idMatch = !EXCLUDED_CATEGORIES.some(excluded =>
                category.id && category.id.toString().toLowerCase().includes(excluded.toLowerCase())
            );

            return nameMatch && idMatch;
        });
    }, [categories]);

    const products = useMemo(() => {
        let loadedProducts = [];

        if (sessionData && sessionData.products && Array.isArray(sessionData.products)) {
            loadedProducts = sessionData.products.filter(product => product.is_totem);
        } else if (isOffline) {
            loadedProducts = OfflineService.getProducts().filter(product => product.is_totem);
        } else {
            loadedProducts = [];
        }

        return loadedProducts;
    }, [sessionData, isOffline]);

    const filterProducts = useCallback(() => {
        setLoading(true);

        try {
            const filtered = products.filter(product => {
                const matchesCategory = categoryId === 'all' || product.category_id === parseInt(categoryId);
                const matchesSubcategory = !subcategoryId || product.subcategory_id === parseInt(subcategoryId);
                return matchesCategory && matchesSubcategory;
            }).sort((a, b) => parseFloat(a.price) - parseFloat(b.price));

            // Intercambiar pares para que quede: 2,1 / 4,3 / 6,5
            const reordered = [];
            for (let i = 0; i < filtered.length; i += 2) {
                if (i + 1 < filtered.length) {
                    reordered.push(filtered[i + 1]);
                    reordered.push(filtered[i]);
                } else {
                    reordered.push(filtered[i]);
                }
            }
            setFilteredProducts(reordered);
        } catch (error) {
            console.error('Error al filtrar productos:', error);
            setFilteredProducts([]);
        } finally {
            setLoading(false);
        }
    }, [categoryId, subcategoryId, products]);

    useEffect(() => {
        if (!filteredProducts.length) return;
        filteredProducts.forEach(product => {
            if (product.id && !bannerCache.has(product.id)) {
                fetch(`http://localhost:9000/api/products/${product.id}/banner`)
                    .then(r => r.json())
                    .then(data => {
                        if (data.success && data.banner_base64) {
                            bannerCache.set(product.id, `data:image/png;base64,${data.banner_base64}`);
                        }
                    })
                    .catch(() => { });
            }
        });
    }, [filteredProducts]);

    useEffect(() => {
        filterProducts();
    }, [filterProducts]);

    const handleAddToCart = (product) => {
        if (product.custom_attributes && product.custom_attributes.length > 0) {
            setSelectedProduct(product);
        } else {
            addItem(product);
        }
    };

    const handleModalSubmit = (selectedQuantities, totalPrice, attributesDescription) => {
        addItem({
            ...selectedProduct,
            price: totalPrice,
            customDescription: attributesDescription,
            selectedAttributes: selectedQuantities,
            id: `${selectedProduct.id}-custom-${Date.now()}`,
            hasOptions: true
        });
        setSelectedProduct(null);
        navigate('/payment');
    };

    const handleModalClose = () => {
        setSelectedProduct(null);
    };

    const handleBackToMenu = () => {
        if (subcategoryId) {
            navigate(`/subcategories/${categoryId}`);
        } else {
            navigate('/');
        }
    };

    if (!sessionData || !sessionData.authenticated) {
        return null;
    }

    const currentCategory = categoryId === 'all'
        ? { name: 'Todos los productos' }
        : getFilteredCategories().find(cat => cat.id === parseInt(categoryId)) || { name: 'Productos' };

    const currentSubcategory = subcategoryId
        ? products.find(p => p.subcategory_id === parseInt(subcategoryId))?.subcategory_name
        : null;

    const displayTitle = currentSubcategory
        ? `${currentCategory.name} - ${currentSubcategory}`
        : currentCategory.name;

    return (
        <div className="pl-screen">
            <div className="pl-header">
                <img src={logo} alt="GetIT" className="pl-logo" />
                <div className="pl-posgo">
                    <img src={logoPosgo} alt="PosGo" className="pl-posgo-logo" />
                    <span className="pl-posgo-text">PosGo.cl</span>
                </div>
            </div>

            <div className="pl-body">
                <h1 className="pl-title">{displayTitle}</h1>

                {loading ? (
                    <div className="pl-loading">
                        <div className="pl-spinner"></div>
                        <p>Cargando productos...</p>
                    </div>
                ) : products.length === 0 ? (
                    <div className="pl-empty">
                        <svg style={{ width: '5rem', height: '5rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                        <p>{isOffline ? 'No hay productos disponibles en modo offline' : 'No se encontraron productos'}</p>
                    </div>
                ) : filteredProducts.length === 0 ? (
                    <div className="pl-empty">
                        <svg style={{ width: '5rem', height: '5rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <p>No se encontraron productos</p>
                    </div>
                ) : (
                    <div className="pl-grid">
                        {filteredProducts.map((product, index) => (
                            <div
                                key={product.id}
                                className="pl-card-anim"
                                style={{ animationDelay: `${index * 0.05}s` }}
                            >
                                <ProductCard
                                    product={product}
                                    onAddToCart={handleAddToCart}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="pl-bottom-bar">
                <button className="pl-back-btn" onClick={handleBackToMenu}>
                    <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    {subcategoryId ? 'Volver a Subcategorías' : 'Volver al menú'}
                </button>

                {totalItems > 0 && (
                    <button className="pl-pay-btn" onClick={() => navigate('/payment')}>
                        Ir a pagar ({totalItems})
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                    </button>
                )}
            </div>

            {selectedProduct && (
                <ProductOptionsModal
                    product={selectedProduct}
                    onClose={handleModalClose}
                    onSubmit={handleModalSubmit}
                />
            )}

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@700;900&display=swap');

                @keyframes fade-in-up {
                    0% { opacity: 0; transform: translateY(20px); }
                    100% { opacity: 1; transform: translateY(0); }
                }

                ::-webkit-scrollbar { width: 6px; }
                ::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 3px; }
                ::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 3px; }
                ::-webkit-scrollbar-thumb:hover { background: #a1a1a1; }

                .pl-screen {
                    min-height: 100vh;
                    display: flex;
                    flex-direction: column;
                    background: #f5f5f5;
                    padding-bottom: 6rem;
                }

                .pl-header {
                    background: white;
                    padding: 1.5rem;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.07);
                    position: relative;
                }

                .pl-logo {
                    height: 100px;
                    width: auto;
                }

                .pl-posgo {
                    position: absolute;
                    right: 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                }

                .pl-posgo-logo {
                    height: 80px;
                    width: auto;
                }

                .pl-posgo-text {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #db2113;
                    font-family: Quicksand, sans-serif;
                }

                .pl-body {
                    flex: 1;
                    padding: 2rem 1.5rem;
                    max-width: 900px;
                    width: 100%;
                    margin: 0 auto;
                }

                .pl-title {
                    font-family: 'Montserrat', sans-serif;
                    font-weight: 900;
                    font-size: 3rem;
                    color: #0E8164;
                    text-align: center;
                    margin: 0 0 1.5rem 0;
                    letter-spacing: -0.02em;
                }

                .pl-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 1.25rem;
                }

                .pl-card-anim {
                    animation: fade-in-up 0.5s ease-out both;
                }

                .pl-loading {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 4rem;
                    gap: 1rem;
                    color: #9ca3af;
                }

                .pl-spinner {
                    width: 48px;
                    height: 48px;
                    border: 4px solid #e5e7eb;
                    border-top-color: #0E8164;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                .pl-empty {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 4rem;
                    gap: 1rem;
                    color: #9ca3af;
                    text-align: center;
                }

                .pl-page-btn {
                    padding: 0.6rem 1.2rem;
                    border: 2px solid #e5e7eb;
                    background: white;
                    border-radius: 12px;
                    font-size: 0.95rem;
                    font-weight: 600;
                    color: #374151;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .pl-page-btn:disabled {
                    opacity: 0.4;
                    cursor: not-allowed;
                }

                .pl-page-num {
                    width: 40px;
                    height: 40px;
                    border: 2px solid #e5e7eb;
                    background: transparent;
                    border-radius: 10px;
                    font-size: 0.95rem;
                    font-weight: 600;
                    color: #374151;
                    display: none;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .pl-page-num.active {
                    color: white;
                    border-color: transparent;
                }

                .pl-ellipsis {
                    padding: 0 4px;
                    color: #9ca3af;
                }

                .pl-bottom-bar {
                    position: fixed;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    background: white;
                    padding: 0.75rem 2rem;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    box-shadow: 0 -4px 16px rgba(0,0,0,0.08);
                    z-index: 40;
                }

                .pl-back-btn {
                    display: flex;
                    align-items: center;
                    gap: 0.6rem;
                    background: #0E8164;
                    color: white;
                    border: none;
                    border-radius: 50px;
                    padding: 0.75rem 2rem;
                    font-size: 1.1rem;
                    font-weight: 800;
                    cursor: pointer;
                    transition: all 0.2s;
                    white-space: nowrap;
                }

                .pl-back-btn:hover {
                    background: #0a6b52;
                }

                .pl-pay-btn {
                    display: flex;
                    align-items: center;
                    gap: 0.6rem;
                    background: #DC2626;
                    color: white;
                    border: none;
                    border-radius: 50px;
                    padding: 0.75rem 2rem;
                    font-size: 1.1rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .pl-pay-btn:hover {
                    background: #B91C1C;
                }
            `}</style>
        </div>
    );
};

export default ProductList;